import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Upload, Activity, HeartPulse, CheckCircle2, Watch, RefreshCw, Link2, Unlink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { sportIcon, fmtDate } from "../lib/workout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";

function Uploader({ title, hint, accept, endpoint, icon: Icon, onDone, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    setBusy(true);
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    try {
      const { data } = await api.post(endpoint, form, { headers: { "Content-Type": "multipart/form-data" } });
      const n = data.results ? data.results.filter((r) => r.ok).length : data.imported_days;
      toast(`Imported ${n} ${data.results ? "activit" + (n === 1 ? "y" : "ies") : "day(s)"}`,
        data.results ? { description: `${data.results.filter((r) => r.matched_workout).length} matched a planned workout` } : undefined);
      onDone();
    } catch {
      toast("Import failed — check the file format");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-3 mb-1">
        <Icon size={20} className="text-brand-orange" />
        <p className="font-heading font-bold text-foreground">{title}</p>
      </div>
      <p className="text-sm text-muted-foreground mb-4">{hint}</p>
      <input ref={inputRef} type="file" multiple accept={accept} onChange={handle} className="hidden" data-testid={`${testid}-input`} />
      <Button onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${testid}-btn`} className="rounded-xl font-semibold">
        <Upload size={16} className="mr-1.5" /> {busy ? "Importing…" : "Choose files"}
      </Button>
    </div>
  );
}

function GarminConnect({ onSynced }) {
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState("idle"); // idle | mfa
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const loadStatus = () => api.get("/garmin/status").then((r) => setStatus(r.data));
  useEffect(() => { loadStatus(); }, []);

  const connect = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/garmin/connect", { email, password });
      if (data.needs_mfa) { setSessionId(data.session_id); setStep("mfa"); toast("Enter the code Garmin just sent you"); }
      else { toast("Garmin connected & synced", { description: `${data.synced.activities} activities, ${data.synced.wellness_days} days` }); finishConnect(); }
    } catch (e) {
      const m = formatApiErrorDetail(e.response?.data?.detail) || "Connection failed";
      setErr(m); toast("Couldn't connect", { description: m });
    } finally { setBusy(false); }
  };

  const submitMfa = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/garmin/mfa", { session_id: sessionId, code });
      toast("Garmin connected & synced", { description: `${data.synced.activities} activities, ${data.synced.wellness_days} days` });
      finishConnect();
    } catch (e) {
      const m = formatApiErrorDetail(e.response?.data?.detail) || "MFA failed";
      setErr(m); toast("MFA failed", { description: m });
    } finally { setBusy(false); }
  };

  const finishConnect = () => {
    setStep("idle"); setPassword(""); setCode(""); setSessionId("");
    loadStatus(); onSynced();
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/garmin/sync");
      toast("Synced", { description: `${data.synced.activities} activities, ${data.synced.wellness_days} days` });
      loadStatus(); onSynced();
    } catch (err) {
      toast(formatApiErrorDetail(err.response?.data?.detail) || "Sync failed");
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    await api.delete("/garmin/disconnect");
    toast("Garmin disconnected");
    loadStatus();
  };

  if (status === null) return <Skeleton className="h-40 w-full rounded-2xl" />;

  if (status.connected) {
    return (
      <div className="rounded-2xl border border-primary/40 bg-primary/5 p-5" data-testid="garmin-connected">
        <div className="flex items-center gap-3 mb-3">
          <div className="brand-gradient w-10 h-10 rounded-xl flex items-center justify-center"><Watch className="text-white" size={20} /></div>
          <div>
            <p className="font-heading font-bold text-foreground">Garmin connected</p>
            <p className="text-xs text-muted-foreground">Last sync: {status.last_sync ? fmtDate(status.last_sync.slice(0, 10)) : "—"}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={syncNow} disabled={busy} data-testid="garmin-sync-btn" className="rounded-xl font-semibold flex-1">
            <RefreshCw size={16} className={`mr-1.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "Syncing…" : "Sync now"}
          </Button>
          <Button variant="outline" onClick={disconnect} data-testid="garmin-disconnect-btn" className="rounded-xl">
            <Unlink size={16} className="mr-1.5" /> Disconnect
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="garmin-connect-card">
      <div className="flex items-center gap-3 mb-1">
        <Watch size={20} className="text-brand-orange" />
        <p className="font-heading font-bold text-foreground">Connect Garmin (live)</p>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Auto-import your activities & daily stats. Your password is used once to get a secure token — we never store it. <span className="text-muted-foreground/80">Unofficial connection, for testing.</span>
      </p>

      {step === "idle" ? (
        <div className="space-y-3">
          <Input data-testid="garmin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Garmin email" className="h-11" />
          <Input data-testid="garmin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Garmin password" className="h-11" />
          <Button onClick={connect} disabled={busy || !email || !password} data-testid="garmin-connect-btn" className="w-full h-11 rounded-xl font-semibold">
            <Link2 size={16} className="mr-1.5" /> {busy ? "Connecting…" : "Connect"}
          </Button>
          {err && <p className="text-sm text-destructive leading-relaxed" data-testid="garmin-error">{err}</p>}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-foreground">Enter the verification code Garmin sent you:</p>
          <Input data-testid="garmin-mfa-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" className="h-11" />
          <Button onClick={submitMfa} disabled={busy || !code} data-testid="garmin-mfa-btn" className="w-full h-11 rounded-xl font-semibold">
            {busy ? "Verifying…" : "Verify & connect"}
          </Button>
          {err && <p className="text-sm text-destructive leading-relaxed" data-testid="garmin-error">{err}</p>}
        </div>
      )}
    </div>
  );
}

export default function Import() {
  const navigate = useNavigate();
  const [activities, setActivities] = useState(null);
  const [stats, setStats] = useState(null);

  const load = () => {
    api.get("/import/activities").then((r) => setActivities(r.data.activities));
    api.get("/import/wellness").then((r) => setStats(r.data.daily_stats));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="import-back">
        <ArrowLeft size={16} /> Back
      </button>

      <div>
        <h1 className="font-heading font-black text-3xl tracking-tight text-foreground" data-testid="import-title">Garmin data</h1>
        <p className="text-muted-foreground mt-2 max-w-xl">
          Connect live for automatic sync, or upload exported files. Caden learns from your real sessions and daily readiness.
        </p>
      </div>

      <GarminConnect onSynced={load} />

      <div className="flex items-center gap-3 pt-2">
        <div className="h-px bg-border flex-1" />
        <span className="text-xs text-muted-foreground uppercase tracking-widest">or upload files</span>
        <div className="h-px bg-border flex-1" />
      </div>

      <Uploader
        title="Activities"
        hint="Upload .fit / .tcx / .gpx files from your workouts. Caden reads distance, pace, HR and power."
        accept=".fit,.tcx,.gpx" endpoint="/import/activities" icon={Activity} onDone={load} testid="import-activities"
      />
      <Uploader
        title="Daily stats (wellness)"
        hint="Upload your Garmin 'Export Your Data' ZIP or wellness JSON — sleep, resting HR, HRV, steps."
        accept=".zip,.json" endpoint="/import/wellness" icon={HeartPulse} onDone={load} testid="import-wellness"
      />

      {/* Imported activities */}
      <div>
        <h2 className="font-heading font-bold text-xl text-foreground mb-3">Imported activities</h2>
        {activities === null ? <Skeleton className="h-16 w-full rounded-2xl" /> :
          activities.length === 0 ? (
            <p className="text-muted-foreground text-sm rounded-2xl border border-dashed border-border p-5 text-center" data-testid="import-activities-empty">Nothing imported yet.</p>
          ) : (
            <div className="space-y-2" data-testid="import-activities-list">
              {activities.map((a, i) => {
                const Icon = sportIcon(a.sport);
                return (
                  <motion.div key={a.id || i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                    className="rounded-2xl border border-border bg-card p-4 flex items-center gap-3">
                    <div className="brand-gradient w-10 h-10 rounded-xl flex items-center justify-center shrink-0"><Icon className="text-white" size={18} /></div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground capitalize truncate">{a.sport} · {(a.distance_m / 1000).toFixed(1)} km</p>
                      <p className="text-xs text-muted-foreground">{fmtDate(a.date)} · {Math.round(a.duration_s / 60)} min{a.avg_hr ? ` · ${a.avg_hr} bpm` : ""}</p>
                    </div>
                    <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
                  </motion.div>
                );
              })}
            </div>
          )}
      </div>

      {/* Daily stats */}
      {stats && stats.length > 0 && (
        <div>
          <h2 className="font-heading font-bold text-xl text-foreground mb-3">Recent daily stats</h2>
          <div className="space-y-2" data-testid="import-wellness-list">
            {stats.slice(0, 10).map((s) => (
              <div key={s.date} className="rounded-2xl border border-border bg-card p-4 flex items-center justify-between text-sm">
                <span className="font-medium text-foreground">{fmtDate(s.date)}</span>
                <span className="text-muted-foreground flex gap-3 flex-wrap justify-end">
                  {s.sleep_hours != null && <span>😴 {s.sleep_hours}h</span>}
                  {s.resting_hr != null && <span>❤ {s.resting_hr}</span>}
                  {s.hrv != null && <span>HRV {s.hrv}</span>}
                  {s.steps != null && <span>👟 {s.steps}</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
